import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Hammer, TrendingUp, MapPin, Check, Handshake, MessageSquare, Scale, Users, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { api, track } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const AREAS = ["Mansfield", "Mansfield Woodhouse", "Kirkby-in-Ashfield", "Hucknall", "Nottingham", "Beeston", "Wollaton", "Ravenshead"];

const PILLARS = [
  { icon: HomeIcon, title: "BUY", desc: "We purchase residential property directly and work with local agents and private vendors." },
  { icon: Hammer, title: "RENOVATE", desc: "We transform tired, dated and neglected properties through thoughtful refurbishment and improvement." },
  { icon: TrendingUp, title: "INVEST", desc: "We build long-term property investments and selectively source opportunities for investors." },
];

const REASONS = [
  { icon: MapPin, text: "Local Nottinghamshire investors" },
  { icon: Users, text: "Experienced landlords" },
  { icon: Hammer, text: "Genuine renovation experience" },
  { icon: MessageSquare, text: "Straightforward communication" },
  { icon: Scale, text: "Sensible, evidence-based offers" },
  { icon: Check, text: "A professional approach throughout" },
  { icon: Handshake, text: "We value long-term relationships with agents and vendors" },
];

export default function Home() {
  const [projects, setProjects] = useState([]);
  useSeo({
    title: "Emerald Crown Property Investments | Nottinghamshire Property Buyers",
    description: "We buy, renovate and invest in residential property across Nottinghamshire. Restoring tired and dated homes in Mansfield, Nottingham, Hucknall and surrounding areas.",
    path: "/",
  });

  useEffect(() => {
    track("page_view", { page: "home" });
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <img
          src="https://images.unsplash.com/photo-1732721176814-c0bc82db3971?auto=format&fit=crop&q=85&w=1600"
          alt="Renovated British residential property"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="max-w-2xl">
            <span className="eyebrow text-brass">Nottinghamshire Property Investors</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-white leading-[1.12] tracking-tight">
              We Buy. We Invest. We Source.
            </h1>
            <p className="mt-6 text-lg text-emerald-50/90 leading-relaxed max-w-xl">
              Emerald Crown Property Investments acquires and renovates residential property and introduces selected investment opportunities to qualified investors.
            </p>
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-brass mb-2">Selling a property?</p>
                <Link to="/sell" onClick={() => track("cta_click", { cta: "hero_sell" })}>
                  <Button data-testid="hero-cta-sell" className="bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-6 h-12 text-base w-full">
                    Tell us about your property <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              </div>
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest text-white/70 mb-2">Property investor?</p>
                <Link to="/investors" onClick={() => track("cta_click", { cta: "hero_investors" })}>
                  <Button data-testid="hero-cta-investors" variant="outline" className="rounded-full px-6 h-12 text-base border-white/40 text-white bg-white/5 hover:bg-white/15 backdrop-blur w-full">
                    Join our investor network <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <span key={a} className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/10 text-white/80 border border-white/15">{a}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 120}>
              <div data-testid={`pillar-${p.title.toLowerCase()}`} className="group h-full bg-white rounded-2xl border border-slate-200/80 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <span className="grid place-items-center w-14 h-14 rounded-xl bg-emerald-50 group-hover:bg-emerald-900 transition-colors">
                  <p.icon className="w-6 h-6 text-emerald-800 group-hover:text-brass transition-colors" />
                </span>
                <h3 className="mt-6 text-xl font-sans font-bold tracking-wide text-emerald-950">{p.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* POTENTIAL EDITORIAL */}
      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="eyebrow text-brass">Our approach</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[2.75rem] font-serif font-medium leading-tight">
              We're not just looking for houses. We're looking for potential.
            </h2>
            <p className="mt-6 text-emerald-50/80 leading-relaxed text-lg">
              Properties don't need to be perfect. In fact, the ones we're most interested in usually aren't.
            </p>
            <p className="mt-4 text-emerald-50/70 leading-relaxed">
              We're interested in properties that may be dated, require refurbishment, have been difficult to sell,
              are inherited, sit vacant, or simply need someone willing to take on the work. Where others see a
              problem, we tend to see a home waiting to be brought back to life.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1553969914-1397d0f0b865?auto=format&fit=crop&q=85&w=600" alt="Property before renovation" className="rounded-xl w-full h-56 object-cover" />
              <img src="https://images.unsplash.com/photo-1714860534425-7ce04e013dec?auto=format&fit=crop&q=85&w=600" alt="Renovated kitchen" className="rounded-xl w-full h-56 object-cover mt-8" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY WORK WITH US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <span className="eyebrow">Why work with us?</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-medium text-slate-900">A local partner you can rely on</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {REASONS.map((r, i) => (
            <Reveal key={r.text} delay={i * 70}>
              <div className="flex items-start gap-3.5 bg-white rounded-xl border border-slate-200/80 p-5 h-full">
                <span className="grid place-items-center w-10 h-10 rounded-lg bg-emerald-50 shrink-0">
                  <r.icon className="w-5 h-5 text-emerald-800" />
                </span>
                <p className="text-slate-700 font-medium leading-snug pt-1.5">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="max-w-2xl">
              <span className="eyebrow">Recent projects</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-serif font-medium text-slate-900">Tired homes, thoughtfully transformed</h2>
            </div>
            <Link to="/projects"><Button variant="outline" className="rounded-full border-emerald-900/30 text-emerald-900 hover:bg-emerald-50" data-testid="home-view-all-projects">View all projects <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-7">
            {projects.map((p, i) => (
              <Reveal key={p.slug} delay={i * 100}>
                <Link to={`/projects/${p.slug}`} data-testid={`home-project-${p.slug}`} className="group block rounded-2xl overflow-hidden border border-slate-200/80 bg-white hover:shadow-xl transition-all duration-300">
                  <div className="relative h-56 overflow-hidden bg-slate-100">
                    {p.hero_img && <img src={p.hero_img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    <span className="absolute top-3 left-3 text-[11px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-full bg-emerald-950/85 text-brass backdrop-blur">{p.status}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500"><Building2 className="w-3.5 h-3.5" />{p.location}</div>
                    <h3 className="mt-2 text-xl font-serif text-slate-900">{p.name}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{p.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-800 group-hover:gap-2.5 transition-all">Read case study <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-3xl bg-emerald-900 px-8 py-14 md:px-16 md:py-20 text-center relative overflow-hidden">
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-serif text-white">Got a property that needs a new lease of life?</h2>
            <p className="mt-4 text-emerald-50/80 max-w-xl mx-auto">Tell us about it. We review every enquiry properly and get back to you with a straightforward, honest conversation.</p>
            <Link to="/sell"><Button data-testid="cta-band-sell" className="mt-8 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-8 h-12 text-base">Sell us your property</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
